import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { JOB_STATUSES, statusLabel, currencySymbol } from "@/lib/constants";
import JobFormDialog from "@/components/JobFormDialog";
import ColumnPicker, { useColumnPrefs } from "@/components/ColumnPicker";

// --- helpers --------------------------------------------------------------
const fmtDT = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${dy} ${hh}:${mm}`;
};
const addrStr = (a) => {
  if (!a) return "";
  const parts = [a.street, a.city, a.state, a.zip].filter(Boolean);
  return parts.join(", ");
};
const isReadyToInvoice = (j) => j.status === "delivered" || j.status === "invoiced";
const isPickupApptFlagged = (j) => !!j.appointment_date;
const isDropoffApptFlagged = (j) => !!j.delivery_date;
const rowStatusTone = (s) => {
  switch (s) {
    case "invoiced": return "text-emerald-800";
    case "delivered": return "text-emerald-700";
    case "in_transit": return "text-sky-700";
    case "scheduled": return "text-indigo-700";
    case "quoted": return "text-blue-700";
    case "requested": return "text-amber-800";
    case "cancelled": return "text-rose-700 line-through";
    default: return "text-zinc-900";
  }
};

// --- fixed column definition ---------------------------------------------
// widths chosen for a compact spreadsheet feel; sum ~2600px
const COLS = [
  { key: "job_number",     label: "Job ID",            w: 96,  type: "link" },
  { key: "carrier_name",   label: "Carrier",           w: 150 },
  { key: "status",         label: "Status",            w: 84 },
  { key: "pickup_date",    label: "Start Time",        w: 120, type: "datetime" },
  { key: "delivery_date",  label: "End Time",          w: 120, type: "datetime" },
  { key: "pickup_name",    label: "Pickup Location",   w: 160 },
  { key: "delivery_name",  label: "DropOff Location",  w: 160 },
  { key: "ready_invoice",  label: "Ready to Invoice",  w: 90,  type: "yesno" },
  { key: "from",           label: "From",              w: 100 },
  { key: "to",             label: "To",                w: 100 },
  { key: "pickup_addr",    label: "Pickup Address",    w: 200 },
  { key: "delivery_addr",  label: "DropOff Address",   w: 200 },
  { key: "bill_to_company",label: "Billing Organization", w: 160 },
  { key: "pk_appt_flag",   label: "Pickup Appt Req.",  w: 90,  type: "flag" },
  { key: "do_appt_flag",   label: "Dropoff Appt Req.", w: 90,  type: "flag" },
  { key: "standing_order", label: "Standing Order ID", w: 100 },
  { key: "pieces",         label: "Pieces",            w: 60,  align: "right" },
  { key: "pallets",        label: "Plts",              w: 50,  align: "right" },
  { key: "weight",         label: "Weight",            w: 70,  align: "right" },
  { key: "commodity",      label: "Commodity",         w: 130 },
  { key: "service_level",  label: "Service Level",     w: 80 },
  { key: "service_type",   label: "Service Type",      w: 100 },
  { key: "cod",            label: "COD Amount",        w: 80,  align: "right" },
  { key: "reference",      label: "Reference #",       w: 110 },
  { key: "revenue",        label: "Revenue",           w: 80,  align: "right" },
  { key: "cost_total",     label: "3PL Cost Total",    w: 90,  align: "right" },
  { key: "billing_org_id", label: "Billing OrgID",     w: 90 },
];

// Compute grid row from a job doc
const toRow = (j) => {
  const palletsUnit = (j.pieces_unit || "").toLowerCase().includes("pallet");
  return {
    ...j,
    pickup_name: j.pickup?.name || "",
    delivery_name: j.delivery?.name || "",
    from: j.pickup?.city || "",
    to: j.delivery?.city || "",
    pickup_addr: addrStr(j.pickup),
    delivery_addr: addrStr(j.delivery),
    ready_invoice: isReadyToInvoice(j),
    pk_appt_flag: isPickupApptFlagged(j),
    do_appt_flag: isDropoffApptFlagged(j),
    standing_order: 0,
    pallets: palletsUnit ? (j.pieces || 0) : 0,
    service_level: "",
    cod: 0,
    revenue: j.rate || 0,
    cost_total: j.rate ? Math.round(j.rate * 0.7) : 0,
    billing_org_id: (j.bill_to_customer_id || j.customer_id || "").slice(-4).toUpperCase(),
  };
};

// --- component -----------------------------------------------------------
export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [editJob, setEditJob] = useState(null); // job to edit
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const {
    hidden, order, setHidden, setOrder, reset: resetHidden, visibleColumns: visibleCols,
  } = useColumnPrefs("apex.jobs.cols", COLS);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q) params.q = q;
      const [j, c, cr] = await Promise.all([
        api.get("/jobs", { params }),
        api.get("/customers"),
        api.get("/carriers").catch(() => ({ data: [] })),
      ]);
      setJobs(j.data);
      setCustomers(c.data);
      setCarriers(cr.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const deleteJob = async (job) => {
    const ok = window.confirm(
      `Delete Job ${job.job_number}?\n\n` +
      `This permanently removes the job AND any associated invoices, rate confirmations, and POD files. This cannot be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/jobs/${job.id}`);
      const extras = [];
      if (data?.invoices_removed) extras.push(`${data.invoices_removed} invoice(s)`);
      if (data?.rate_confirmations_removed) extras.push(`${data.rate_confirmations_removed} rate confirmation(s)`);
      toast.success(`Job ${job.job_number} deleted${extras.length ? ` — also removed ${extras.join(", ")}` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const rows = useMemo(() => jobs.map(toRow), [jobs]);

  const cellValue = (r, col) => {
    const v = r[col.key];
    if (col.type === "datetime") return fmtDT(v);
    if (col.type === "yesno") return v ? "Yes" : "";
    if (col.type === "flag") return v ? "✓" : "";
    if (col.key === "status") return statusLabel(r.status);
    if (col.key === "revenue" || col.key === "cost_total" || col.key === "cod") {
      return v ? `${currencySymbol(r.currency)}${Number(v).toLocaleString()}` : "";
    }
    if (col.key === "weight") return v ? Number(v).toLocaleString() : "";
    return v ?? "";
  };

  const totalWidth = visibleCols.reduce((a, c) => a + c.w, 0) + 60; // +action col (edit + delete)

  return (
    <div>
      {/* toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
        <div className="flex-1 flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search job #, PO #, customer..."
              className="sharp apex-input h-10 pl-9"
              data-testid="jobs-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sharp h-10 w-48" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {JOB_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" className="sharp h-10 border-zinc-300" data-testid="jobs-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <ColumnPicker
            columns={COLS}
            order={order}
            hidden={hidden}
            onOrderChange={setOrder}
            onHiddenChange={setHidden}
            onReset={resetHidden}
            lockedKeys={["job_number", "status"]}
            testId="jobs-columns"
          />
          <Button onClick={() => setOpenNew(true)} className="sharp bg-[#0056B3] hover:bg-[#004494] h-10" data-testid="jobs-new-btn">
            <Plus className="h-4 w-4 mr-2" /> New job
          </Button>
        </div>
        <div className="text-[11px] text-zinc-600 font-mono px-2 ml-auto">
          {rows.length} rows · double-click any row to edit
        </div>
      </div>

      {/* dense spreadsheet grid */}
      <div className="border border-zinc-400 bg-white overflow-auto max-h-[calc(100vh-14rem)]" data-testid="jobs-grid">
        <table
          className="text-[11px] border-collapse"
          style={{ minWidth: totalWidth, fontFamily: "'Segoe UI','IBM Plex Sans',system-ui,sans-serif" }}
        >
          <thead className="bg-[#f2f2f2] sticky top-0 z-10">
            <tr>
              <th
                className="border border-zinc-400 px-1.5 py-1 text-left sticky left-0 bg-[#f2f2f2] z-20"
                style={{ width: 60, minWidth: 60 }}
              >&nbsp;</th>
              {visibleCols.map((c) => (
                <th
                  key={c.key}
                  className="border border-zinc-400 px-1.5 py-1 text-left font-semibold text-zinc-800 align-bottom"
                  style={{ width: c.w, minWidth: c.w, maxWidth: c.w }}
                >
                  <div className="whitespace-normal leading-tight">{c.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleCols.length + 1} className="p-6 text-center mono-label">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={visibleCols.length + 1} className="p-6 text-center mono-label">No jobs found</td></tr>
            ) : rows.map((r) => {
              const selected = selectedId === r.id;
              return (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  onDoubleClick={() => setEditJob(r)}
                  className={`cursor-pointer ${selected ? "bg-[#cfe4ff]" : "hover:bg-[#f7f9fc]"}`}
                  data-testid={`job-row-${r.job_number}`}
                >
                  {/* sticky action column */}
                  <td
                    className={`border border-zinc-300 text-center sticky left-0 ${selected ? "bg-[#cfe4ff]" : "bg-white"}`}
                    style={{ width: 60, minWidth: 60 }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditJob(r); }}
                        className="p-1 hover:text-[#0056B3]"
                        title="Edit job"
                        data-testid={`edit-job-${r.job_number}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteJob(r); }}
                        className="p-1 text-zinc-500 hover:text-rose-700"
                        title="Delete job"
                        data-testid={`delete-job-${r.job_number}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>

                  {visibleCols.map((c) => {
                    const align = c.align === "right" ? "text-right" : "text-left";
                    const isJobId = c.key === "job_number";
                    const isStatus = c.key === "status";
                    const isFlag = c.type === "flag";
                    return (
                      <td
                        key={c.key}
                        className={`border border-zinc-300 px-1.5 py-0.5 ${align} truncate ${isStatus ? rowStatusTone(r.status) : ""}`}
                        style={{ width: c.w, minWidth: c.w, maxWidth: c.w }}
                        title={String(cellValue(r, c) ?? "")}
                      >
                        {isJobId ? (
                          <Link
                            to={`/admin/jobs/${r.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[#0056B3] underline underline-offset-2 font-mono"
                          >
                            {r.job_number}
                          </Link>
                        ) : isFlag ? (
                          <span className={r[c.key] ? "text-emerald-700 font-bold" : "text-zinc-400"}>
                            {r[c.key] ? "✓" : ""}
                          </span>
                        ) : (
                          <span className={c.type === "datetime" ? "font-mono text-[10.5px]" : ""}>
                            {cellValue(r, c)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <JobFormDialog
        open={openNew}
        onOpenChange={setOpenNew}
        onCreated={load}
        customers={customers}
        carriers={carriers}
        asAdmin
      />
      {/* Edit dialog */}
      <JobFormDialog
        open={!!editJob}
        onOpenChange={(v) => { if (!v) setEditJob(null); }}
        onCreated={load}
        customers={customers}
        carriers={carriers}
        asAdmin
        job={editJob}
      />
    </div>
  );
}
